import { createFileRoute, Link } from "@tanstack/react-router";
import { Search, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { listTemplesSummary, listVisitTempleIds } from "@/lib/yatra-db";
import { useAuth } from "@/lib/auth";
import { useI18n, pickName } from "@/lib/i18n";

type Temple = {
  id: string;
  name_en: string;
  name_hi: string;
  name_gu: string;
  area: string;
  yatra_order: number;
  mool_nayak: string;
};

export const Route = createFileRoute("/app/temples/")({ component: TemplesScreen });

function TemplesScreen() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [tab, setTab] = useState<"all" | "visited" | "pending">("all");
  const [q, setQ] = useState("");
  const [temples, setTemples] = useState<Temple[]>([]);
  const [visited, setVisited] = useState<Set<string>>(new Set());

  useEffect(() => {
    (async () => {
      setTemples(listTemplesSummary());
      if (user) {
        setVisited(new Set(listVisitTempleIds(user.id)));
      }
    })();
  }, [user]);

  const list = temples.filter((tp) => {
    const done = visited.has(tp.id);
    if (tab === "visited" && !done) {
      return false;
    }
    if (tab === "pending" && done) {
      return false;
    }
    const name = pickName(tp, lang);
    return (name + tp.area).toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div className="px-5 pt-16">
      <div className="mb-4">
        <span className="font-serif text-2xl font-bold text-foreground">{t("temples")}</span>
        <div className="text-[11px] text-muted-foreground">Pavitra Derasars of Ahmedabad</div>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search temple, area..."
          className="w-full rounded-full border border-border bg-card py-2.5 pl-9 pr-4 text-sm shadow-sm outline-none transition-all focus:border-primary focus:shadow-[var(--shadow-warm)]"
        />
      </div>
      <div className="mt-3 flex gap-2">
        {(["all", "visited", "pending"] as const).map((tt) => (
          <button
            key={tt}
            onClick={() => setTab(tt)}
            className={`flex-1 rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition-all ${tab === tt ? "bg-primary text-primary-foreground shadow-[var(--shadow-warm)]" : "border border-border bg-card text-muted-foreground hover:bg-muted"}`}
          >
            {tt === "all"
              ? `All (${temples.length})`
              : tt === "visited"
                ? `${t("visited")} (${visited.size})`
                : `${t("pending")} (${temples.length - visited.size})`}
          </button>
        ))}
      </div>
      <div className="mt-4 space-y-2 stagger">
        {list.map((tp) => {
          const done = visited.has(tp.id);
          return (
            <Link
              key={tp.id}
              to="/app/temples/$id"
              params={{ id: tp.id }}
              className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 transition-all hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98]"
            >
              <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary font-serif font-bold text-primary-foreground shadow-md">
                {tp.yatra_order}
                {done && (
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[8px] text-primary-foreground ring-2 ring-card">
                    ✓
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold leading-tight truncate">
                  {pickName(tp, lang)}
                </div>
                <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  {tp.area}
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${done ? "border border-primary/25 bg-primary/12 text-primary" : "bg-primary/12 text-primary"}`}
                  >
                    {done ? t("visited") : t("pending")}
                  </span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[9px] font-semibold text-foreground/70">
                    {tp.mool_nayak}
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
        {list.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">
            No temples found
          </div>
        )}
      </div>
      <div className="h-6" />
    </div>
  );
}
